import { calculateAccuracy } from "@helpers/common";
import { useAppStore } from "@hooks/useAppStore";
import { formatNumber, titleCase } from "@memorang/helpers/common";
import { router, useLocalSearchParams } from "expo-router";
import pluralize from "pluralize";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { MixedItemTag } from "types/tag";
import type { SortBy, SortByDataType } from "./types";

const useTopicsSort = (tags?: MixedItemTag[]) => {
	const [sortOrderType, setSortOrderType] = useState<"asc" | "desc">("asc");
	const [sortBy, setSortBy] = useState<SortBy>("default");

	const {
		id: numericId,
		examName,
		type,
	} = useLocalSearchParams<{
		id: string;
		examName: string;
		type: string;
	}>();

	const practiceTestLabel = useAppStore(
		(state) => state.exam.practiceTest?.label,
	);
	const [showSortModal, setShowSortModal] = useState(false);

	const [localTags, setLocalTags] = useState<MixedItemTag[]>([]);

	const handleShowSortModal = () => {
		setShowSortModal(true);
	};

	const handleHideSortModal = () => {
		setShowSortModal(false);
	};
	const data: SortByDataType[] = [
		{
			label: "Default",
			icon: "format-list-bulleted",
			type: "default",
		},
		{
			label: "Accuracy",
			icon: "bullseye",
			type: "accuracy",
		},
		{
			label: "Topic",
			icon: "tag-multiple-outline",
			type: "topic",
		},
		{
			label: "Correct",
			icon: "check",
			type: "correct",
		},
		{
			label: "Incorrect",
			icon: "close",
			type: "incorrect",
		},
	];

	const sortTags = useCallback(() => {
		const recursiveSort = (tagsToSort: MixedItemTag[]): MixedItemTag[] => {
			const sorted = [...tagsToSort];

			switch (sortBy) {
				case "accuracy":
					sorted.sort((a, b) => {
						const aAccuracy = calculateAccuracy({
							numCorrect: a.numCorrect ?? 0,
							numTotal: a.numItems,
						});
						const bAccuracy = calculateAccuracy({
							numCorrect: b.numCorrect ?? 0,
							numTotal: b.numItems,
						});
						return sortOrderType === "asc"
							? aAccuracy - bAccuracy
							: bAccuracy - aAccuracy;
					});
					break;
				case "topic":
					sorted.sort((a, b) => {
						const comparison = a.label!.localeCompare(b.label!);
						return sortOrderType === "asc" ? comparison : -comparison;
					});
					break;
				case "correct":
					sorted.sort((a, b) => {
						const comparison = (a.numCorrect ?? 0) - (b.numCorrect ?? 0);
						return sortOrderType === "asc" ? comparison : -comparison;
					});
					break;
				case "incorrect":
					sorted.sort((a, b) => {
						const comparison = (a.numIncorrect ?? 0) - (b.numIncorrect ?? 0);
						return sortOrderType === "asc" ? comparison : -comparison;
					});
					break;
				case "default":
					if (sortOrderType === "desc") {
						sorted.reverse();
					}
					break;
				default:
					break;
			}

			return sorted.map((tag) => {
				if (tag.children && tag.children.length > 0) {
					return { ...tag, children: recursiveSort(tag.children) };
				}
				return tag;
			});
		};

		const sortedTags = recursiveSort(tags ?? []);
		setLocalTags(sortedTags);
	}, [sortBy, sortOrderType, tags]);

	// Re-sort when either sort parameter changes
	useEffect(() => {
		sortTags();
	}, [sortTags]);

	const handleSelectSortByType = (type: SortBy) => {
		if (type === sortBy) {
			setSortOrderType((prev) => (prev === "asc" ? "desc" : "asc"));
		} else {
			setSortBy(type);
			setSortOrderType("asc");
		}
		handleHideSortModal();
	};

	const handleSortPress = () => {
		handleShowSortModal();
	};

	const selectedSortByItem = data.find((item) => item.type === sortBy);

	const numTopics = localTags.length;
	const numItems = localTags.reduce((acc, tag) => acc + tag.numItems, 0);

	const title = useMemo(() => {
		return `${numTopics} ${pluralize(type ? titleCase(type) : "Topic", numTopics)} | ${formatNumber(numItems)} ${pluralize("Item", numItems)}`;
	}, [numTopics, numItems, type]);

	const numTotalTopics = useMemo(() => {
		return localTags.length;
	}, [localTags]);

	const numTotalItems = useMemo(() => {
		return localTags.reduce((acc, tag) => acc + tag.numItems, 0);
	}, [localTags]);

	const numCorrectItems = useMemo(() => {
		return localTags.reduce((acc, tag) => acc + (tag.numCorrect ?? 0), 0);
	}, [localTags]);

	const numIncorrectItems = useMemo(() => {
		return localTags.reduce((acc, tag) => acc + (tag.numIncorrect ?? 0), 0);
	}, [localTags]);

	const numSkippedItems = useMemo(() => {
		return localTags.reduce((acc, tag) => acc + (tag.numSkipped ?? 0), 0);
	}, [localTags]);

	const isProgressPage = window.location?.pathname?.includes("progress");
	const fromStudyPack = window.location?.pathname?.includes("study-pack");

	const breadcrumbs = [
		...(examName
			? [
					{
						label: practiceTestLabel ?? "Practice Tests",
						href: "/practice-tests",
						action: () => router.push("/practice-tests"),
					},
					{
						label: examName,
						action: () => router.push(`/session/${numericId}/report/`),
					},
					{
						label: numericId ? `Test #${numericId}` : "Report",
						href: `/session/${numericId}/report/`,
						action: () => router.push(`/session/${numericId}/report/`),
					},
				]
			: []),
		...(isProgressPage
			? [
					{
						label: "Progress",
						href: "/progress",
						action: () => router.push("/progress"),
					},
					...(fromStudyPack
						? [{ label: "Study Pack" }]
						: [
								{
									label: practiceTestLabel ?? "Practice Tests",
									action: () => router.push("/practice-tests"),
								},
							]),
				]
			: []),
	];
	return {
		data,
		sortOrderType,
		showSortModal,
		handleHideSortModal,
		handleSortPress,
		handleSelectSortByType,
		sortBy,
		localTags,
		selectedSortByItem,
		title,
		numTotalTopics,
		numTotalItems,
		numCorrectItems,
		numIncorrectItems,
		numSkippedItems,
		breadcrumbs,
	};
};

export default useTopicsSort;
